import React, { useCallback, useEffect, useState } from 'react';
import {
  Users, Crown, Gem, Loader2, LogOut, Check, X, Shield, UserPlus, Send, Link2,
  MessageSquare, Trophy, Swords, Sparkles, Flame,
} from 'lucide-react';

import { gbApi, gbError, gbFmt } from './gbApi';
import { gbAviso } from './GbNotice';

export const ClanMedal = ({ clan, className = 'h-8 w-8', testid }) => (
  clan?.medal_img
    ? <img src={clan.medal_img} alt={clan.medal} data-testid={testid}
      className={`${className} flex-none object-contain drop-shadow-[0_0_6px_rgba(255,190,60,.35)]`} />
    : <span className={`${className} flex-none rounded-lg border border-black/40`}
      style={{ background: clan?.hex }} data-testid={testid} />
);

const Medalha = ({ medalha, ativa, onPick }) => (
  <button type="button" disabled={!medalha.livre} onClick={() => onPick(medalha.id)}
    data-testid={`gb-clan-medal-${medalha.id}`}
    title={medalha.livre ? medalha.nome : `${medalha.nome} — já usada por outro clã`}
    className={`relative flex flex-col items-center gap-1 rounded-2xl border-2 px-1 pb-1.5 pt-2 transition-all
      ${ativa ? 'border-amber-400 bg-amber-500/10 shadow-[0_0_18px_rgba(251,191,36,.45)]'
        : 'border-purple-900/70 bg-[#0e071a]/80'}
      ${medalha.livre ? 'hover:-translate-y-0.5 hover:border-amber-400/70' : 'cursor-not-allowed'}`}>
    <img src={medalha.img} alt={medalha.nome}
      className={`h-12 w-12 object-contain transition-all ${medalha.livre
        ? 'drop-shadow-[0_0_10px_rgba(255,190,60,.5)]'
        : 'opacity-25 grayscale brightness-50'}`} />
    {medalha.tank_img && (
      // o dono vê o tanque já pintado: a medalha escolhida é a cor do clã na batalha
      <img src={medalha.tank_img} alt="" data-testid={`gb-clan-tank-${medalha.id}`}
        className={`h-7 w-7 object-contain ${medalha.livre ? '' : 'opacity-25 grayscale'}`} />
    )}
    <span className={`w-full truncate text-center text-[9px] font-black ${medalha.livre ? 'text-amber-200' : 'text-purple-400/50'}`}>
      {medalha.nome}
    </span>
    {medalha.cor && (
      <span className="flex w-full items-center justify-center gap-1 text-[8px] font-black uppercase tracking-wider text-purple-200/75">
        <span className="h-2 w-2 flex-none rounded-full border border-white/30" style={{ background: medalha.hex }} />
        {medalha.cor}
      </span>
    )}
    {!medalha.livre && (
      <span className="absolute inset-x-1 top-[38%] rounded bg-black/80 py-[1px] text-center text-[8px] font-black uppercase tracking-wider text-rose-300">
        em uso
      </span>
    )}
    {ativa && (
      <span className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full bg-amber-400 text-[#1b0d02]">
        <Check className="h-3 w-3" />
      </span>
    )}
  </button>
);

const Passo = ({ n, titulo, sub, pronto, children, testid }) => (
  <div className="rounded-2xl border border-purple-900/70 bg-[#0e071a]/70 p-3.5" data-testid={testid}>
    <div className="flex items-center gap-2.5">
      <span className={`flex h-7 w-7 flex-none items-center justify-center rounded-full border text-[11px] font-black transition-colors ${
        pronto ? 'border-emerald-500/70 bg-emerald-500/20 text-emerald-300'
          : 'border-amber-600/60 bg-amber-950/40 text-amber-300'}`}>
        {pronto ? <Check className="h-3.5 w-3.5" /> : n}
      </span>
      <div className="min-w-0">
        <p className="text-xs font-black uppercase tracking-wider text-amber-200">{titulo}</p>
        {sub && <p className="text-[10px] leading-snug text-purple-300/60">{sub}</p>}
      </div>
    </div>
    <div className="mt-3">{children}</div>
  </div>
);

const Chip = ({ Icon, children, tone = 'purple', testid }) => {
  const tons = {
    purple: 'border-purple-800/60 bg-[#150a24]/80 text-purple-200',
    amber: 'border-amber-700/50 bg-amber-950/30 text-amber-200',
    emerald: 'border-emerald-800/50 bg-emerald-950/30 text-emerald-200',
    cyan: 'border-cyan-800/50 bg-cyan-950/30 text-cyan-200',
  };
  return (
    <span data-testid={testid}
      className={`flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-[10px] font-black ${tons[tone]}`}>
      {Icon && <Icon className="h-3 w-3" />}{children}
    </span>
  );
};

const Avatar = ({ src, nome, className = 'h-9 w-9', cor }) => (
  src
    ? <img src={src} alt="" className={`${className} flex-none rounded-full border-2 object-cover`}
      style={{ borderColor: cor || 'rgba(251,191,36,.6)' }} />
    : <span className={`${className} flex flex-none items-center justify-center rounded-full border-2 bg-purple-900/80 text-[11px] font-black text-amber-200`}
      style={{ borderColor: cor || 'rgba(251,191,36,.45)' }}>
      {(nome || '?').slice(0, 2).toUpperCase()}
    </span>
);

export const GbClans = ({ meuId, onChange }) => {
  const [dados, setDados] = useState(null);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ name: '', medal: '' });
  const [recado, setRecado] = useState('');

  const carregar = useCallback(async () => {
    try {
      const { data } = await gbApi.get('/clans');
      setDados(data);
    } catch (e) { gbAviso.erro(gbError(e)); }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  const acao = async (fn) => {
    setBusy(true);
    try { await fn(); await carregar(); onChange?.(); }
    catch (e) { gbAviso.erro(gbError(e)); }
    finally { setBusy(false); }
  };

  if (!dados) {
    return <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-orange-400" /></div>;
  }

  const { my_clan: meu, clans: lista, medals: medalhas, rules: regras, me } = dados;
  const escolhida = (medalhas || []).find((m) => m.id === form.medal);
  const podeCriar = me.level >= regras.min_level && me.diamonds >= regras.cost && regras.slots_left > 0 && !meu;
  const souLider = !!meu && meu.leader === meuId;
  const lotacao = meu ? Math.round((meu.members_count / regras.max_members) * 100) : 0;

  return (
    <div className="pb-10" data-testid="gb-clans">
      {/* ── cabeçalho do hub ── */}
      <div className="relative overflow-hidden rounded-3xl border border-purple-800/60"
        style={{ backgroundImage: 'url(/gb/map_castle.jpg)', backgroundSize: 'cover', backgroundPosition: 'center 30%' }}>
        <div className="bg-[linear-gradient(100deg,rgba(11,7,18,.96)_10%,rgba(11,7,18,.82)_55%,rgba(11,7,18,.5)_100%)] px-4 py-5">
          <p className="flex items-center gap-2 text-[10px] font-black uppercase tracking-[0.28em] text-orange-400/80">
            <Shield className="h-3.5 w-3.5" />irmandades da arena
          </p>
          <h2 className="mt-1 text-2xl font-black leading-none tracking-tight text-amber-100 sm:text-3xl">
            CLÃS <span className="text-orange-400">DO EVENTO</span>
          </h2>
          <div className="mt-3 flex flex-wrap items-center gap-1.5">
            <Chip Icon={Shield} tone="amber">{lista.length}/{regras.max_clans} fundados</Chip>
            <Chip Icon={Sparkles} tone={regras.slots_left > 0 ? 'emerald' : 'purple'}>
              {regras.slots_left} vaga(s) livre(s)
            </Chip>
            <Chip Icon={Users}>até {regras.max_members} membros</Chip>
            <Chip Icon={Flame} tone="amber">você: nível {me.level}</Chip>
            <Chip Icon={Gem} tone="cyan">{gbFmt(me.diamonds)}</Chip>
          </div>
        </div>
      </div>

      {/* ── meu clã ── */}
      {meu && (
        <div className="mt-4 overflow-hidden rounded-3xl border-2" data-testid="gb-my-clan"
          style={{ borderColor: `${meu.hex}66`, background: `linear-gradient(180deg, ${meu.hex}1f, rgba(14,7,26,.92) 55%)` }}>
          <div className="flex flex-wrap items-center gap-3 border-b border-black/40 px-4 py-4">
            <div className="relative flex-none">
              <ClanMedal clan={meu} className="h-16 w-16" testid="gb-my-clan-medal" />
              <span className="absolute -inset-2 -z-10 rounded-full blur-xl" style={{ background: `${meu.hex}55` }} />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-[10px] font-black uppercase tracking-[0.25em]" style={{ color: meu.hex }}>
                meu clã
              </p>
              <h3 className="truncate text-lg font-black leading-tight text-purple-50">{meu.name}</h3>
              <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                <Chip Icon={Trophy} tone="emerald">{gbFmt(meu.pvp_wins)} vit. PvP</Chip>
                <Chip Icon={Swords} tone="amber">{gbFmt(meu.points)} pts</Chip>
                <Chip Icon={Users}>{meu.members_count}/{regras.max_members}</Chip>
                {meu.cor_nome && (
                  <span className="flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-[10px] font-black"
                    style={{ borderColor: `${meu.hex}80`, background: `${meu.hex}1f`, color: meu.hex }}>
                    {meu.tank_img
                      ? <img src={meu.tank_img} alt="" className="h-5 w-5 object-contain" data-testid="gb-my-clan-tank" />
                      : <span className="h-2.5 w-2.5 rounded-full" style={{ background: meu.hex }} />}
                    tanques {meu.cor_nome}
                  </span>
                )}
              </div>
              <span className="mt-2 block h-1.5 w-full max-w-xs overflow-hidden rounded-full bg-black/50">
                <span className="block h-full rounded-full" style={{ width: `${lotacao}%`, background: meu.hex }} />
              </span>
            </div>
            {souLider ? (
              <button onClick={() => window.confirm('Dissolver o clã? Isso libera a cor, a medalha e a vaga.')
                && acao(() => gbApi.delete(`/clans/${meu.clan_id}`))}
                disabled={busy} data-testid="gb-clan-dissolve"
                className="flex flex-none items-center gap-1.5 rounded-full border border-rose-700/60 bg-rose-950/30 px-4 py-2 text-[11px] font-black text-rose-300 transition-colors hover:bg-rose-900/40">
                <Flame className="h-3.5 w-3.5" />DISSOLVER
              </button>
            ) : (
              <button onClick={() => acao(() => gbApi.post('/clans/leave'))} disabled={busy}
                data-testid="gb-clan-leave"
                className="flex flex-none items-center gap-1.5 rounded-full border border-purple-700/60 bg-black/40 px-4 py-2 text-[11px] font-black text-purple-200 transition-colors hover:border-rose-600 hover:text-rose-200">
                <LogOut className="h-3.5 w-3.5" />SAIR DO CLÃ
              </button>
            )}
          </div>

          {souLider && (meu.requests || []).length > 0 && (
            <div className="mx-4 mt-4 rounded-2xl border border-amber-700/50 bg-amber-950/20 p-3" data-testid="gb-clan-requests">
              <p className="mb-2 flex items-center gap-1.5 text-[10px] font-black uppercase tracking-wider text-amber-200/80">
                <UserPlus className="h-3.5 w-3.5" />pedidos para entrar ({meu.requests.length})
              </p>
              <div className="grid gap-2 md:grid-cols-2">
                {meu.requests.map((r) => (
                  <div key={r.kako_id} className="flex items-center gap-2 rounded-xl border border-amber-900/40 bg-black/40 px-2.5 py-2">
                    <Avatar src={r.avatar} nome={r.nickname} className="h-8 w-8" />
                    <span className="min-w-0 flex-1 truncate text-xs font-bold text-purple-100">{r.nickname || r.kako_id}</span>
                    <button onClick={() => acao(() => gbApi.post(`/clans/${meu.clan_id}/approve`, { kako_id: r.kako_id }))}
                      data-testid={`gb-clan-approve-${r.kako_id}`} title="aceitar"
                      className="rounded-full bg-emerald-600 p-1.5 text-white transition-transform hover:scale-110">
                      <Check className="h-3.5 w-3.5" />
                    </button>
                    <button onClick={() => acao(() => gbApi.post(`/clans/${meu.clan_id}/reject`, { kako_id: r.kako_id }))}
                      data-testid={`gb-clan-reject-${r.kako_id}`} title="recusar"
                      className="rounded-full bg-rose-600 p-1.5 text-white transition-transform hover:scale-110">
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="grid gap-3 p-4 lg:grid-cols-2">
            {/* mural */}
            <div className="flex flex-col rounded-2xl border border-purple-900/70 bg-[#0e071a]/85 p-3.5" data-testid="gb-clan-mural">
              <p className="flex items-center gap-1.5 text-[10px] font-black uppercase tracking-wider text-purple-200/70">
                <MessageSquare className="h-3.5 w-3.5 text-orange-400" />mural do clã
              </p>
              <div className="mt-2.5 max-h-52 flex-1 space-y-2 overflow-y-auto overscroll-contain pr-1">
                {(meu.mural || []).length === 0 && (
                  <p className="rounded-xl border border-dashed border-purple-800/60 px-3 py-6 text-center text-[11px] text-purple-300/50">
                    Nenhum recado ainda. Combine o horário de duelar com o time!
                  </p>
                )}
                {(meu.mural || []).slice().reverse().map((m) => (
                  <div key={m.id} className={`max-w-[92%] rounded-2xl px-3 py-2 ${
                    m.kako_id === meuId ? 'ml-auto bg-orange-500/15' : 'bg-purple-950/50'}`}>
                    <p className="text-[10px] font-black text-amber-300/85">
                      {m.nickname}
                      <span className="ml-1.5 font-normal text-purple-300/40">
                        {new Date(m.at).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </p>
                    <p className="whitespace-pre-wrap break-words text-xs text-purple-50">{m.text}</p>
                  </div>
                ))}
              </div>
              <form className="mt-2.5 flex gap-2" onSubmit={(e) => {
                e.preventDefault();
                if (!recado.trim()) return;
                acao(async () => { await gbApi.post('/clans/mural', { text: recado }); setRecado(''); });
              }}>
                <input value={recado} onChange={(e) => setRecado(e.target.value)} maxLength={220}
                  placeholder="Escreva no mural…" data-testid="gb-mural-input"
                  className="min-w-0 flex-1 rounded-xl border border-purple-800/60 bg-[#120a20] px-3 py-2 text-xs text-purple-50 outline-none transition-colors focus:border-orange-500" />
                <button type="submit" disabled={busy || !recado.trim()} data-testid="gb-mural-send"
                  className="rounded-xl bg-gradient-to-r from-orange-500 to-amber-400 px-3.5 text-[#1b0d02] transition-transform hover:scale-105 disabled:opacity-40 disabled:hover:scale-100">
                  <Send className="h-4 w-4" />
                </button>
              </form>
              {souLider && (
                <button onClick={() => acao(async () => {
                  const { data } = await gbApi.post(`/clans/${meu.clan_id}/invite`);
                  const link = `${window.location.origin}/arena?cla=${data.code}`;
                  try { await navigator.clipboard.writeText(link); gbAviso.ok('Link de convite copiado!'); }
                  catch (e) { gbAviso.info(link); }
                })} data-testid="gb-clan-invite"
                  className="mt-2.5 flex items-center justify-center gap-1.5 rounded-xl border border-cyan-800/50 bg-cyan-950/25 py-2 text-[11px] font-black text-cyan-300 transition-colors hover:bg-cyan-900/30">
                  <Link2 className="h-3.5 w-3.5" />copiar link de convite
                </button>
              )}
            </div>

            {/* tropa */}
            <div className="rounded-2xl border border-purple-900/70 bg-[#0e071a]/85 p-3.5">
              <p className="flex items-center gap-1.5 text-[10px] font-black uppercase tracking-wider text-purple-200/70">
                <Users className="h-3.5 w-3.5 text-orange-400" />tropa
                <span className="ml-auto font-mono text-amber-300">{meu.members_count}/{regras.max_members}</span>
              </p>
              <div className="mt-2.5 max-h-72 space-y-2 overflow-y-auto overscroll-contain pr-1">
                {meu.members.slice().sort((a, b) => (b.leader ? 1 : 0) - (a.leader ? 1 : 0) || b.points - a.points).map((m) => (
                  <div key={m.kako_id} data-testid={`gb-clan-member-${m.kako_id}`}
                    className="flex items-center gap-2.5 rounded-xl border border-purple-900/60 bg-black/40 p-2.5 transition-colors hover:border-purple-700/60">
                    <Avatar src={m.avatar} nome={m.nickname} cor={m.leader ? '#fbbf24' : meu.hex} />
                    <div className="min-w-0 flex-1">
                      <p className="flex items-center gap-1 truncate text-xs font-black text-purple-50">
                        {m.leader && <Crown className="h-3.5 w-3.5 flex-none text-amber-300" />}
                        {m.nickname}
                      </p>
                      <p className="mt-0.5 flex flex-wrap items-center gap-x-2 text-[10px] font-bold text-purple-300/55">
                        <span>Nv {m.level}</span>
                        <span className="text-emerald-300/70">{m.pvp_wins} vit.</span>
                        <span className="text-amber-300/70">{gbFmt(m.points)} pts</span>
                      </p>
                    </div>
                    {souLider && !m.leader && (
                      <button onClick={() => acao(() => gbApi.post(`/clans/${meu.clan_id}/kick`, { kako_id: m.kako_id }))}
                        data-testid={`gb-clan-kick-${m.kako_id}`}
                        className="flex-none rounded-full border border-rose-800/60 px-2.5 py-1 text-[10px] font-black text-rose-300 transition-colors hover:bg-rose-900/40">
                        expulsar
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── fundar clã ── */}
      {!meu && (
        <div className="mt-4 rounded-3xl border border-purple-700/50 bg-[#12091f]/90 p-4 shadow-[0_0_40px_rgba(11,7,18,.8)]"
          data-testid="gb-clan-create">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h3 className="flex items-center gap-1.5 text-base font-black text-orange-300">
                <Shield className="h-4 w-4" />FUNDAR UM CLÃ
              </h3>
              <p className="mt-0.5 text-[11px] text-purple-300/60">
                A medalha escolhida define a cor dos tanques de todos os membros na batalha.
              </p>
            </div>
            <Chip Icon={Gem} tone="cyan" testid="gb-clan-cost">{gbFmt(regras.cost)}</Chip>
          </div>

          <div className="mt-3.5 grid gap-3">
            <Passo n={1} titulo="nome do clã"
              sub={`até ${regras.max_name || 14} letras — quem identifica o clã é a medalha`}
              pronto={!!form.name} testid="gb-clan-step-1">
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Nome do clã" maxLength={regras.max_name || 14} data-testid="gb-clan-name"
                className="w-full rounded-xl border border-purple-800/60 bg-[#120a20] px-3 py-2.5 text-sm text-purple-50 outline-none transition-colors focus:border-orange-500" />
            </Passo>

            <Passo n={2} titulo="medalha do clã"
              sub="definitiva — só um clã pode ter cada medalha, e ela já define a cor dos tanques"
              pronto={!!form.medal} testid="gb-clan-step-2">
              <div className="grid max-h-[290px] grid-cols-4 gap-1.5 overflow-y-auto overscroll-contain pr-1 sm:grid-cols-6 lg:grid-cols-8"
                data-testid="gb-clan-medals">
                {(medalhas || []).map((m) => (
                  <Medalha key={m.id} medalha={m} ativa={form.medal === m.id}
                    onPick={(id) => setForm({ ...form, medal: id })} />
                ))}
              </div>
              <div className="mt-2.5 flex items-center gap-2.5 rounded-xl border px-3 py-2"
                style={{ borderColor: `${escolhida?.hex || '#5b21b6'}66`, background: `${escolhida?.hex || '#5b21b6'}14` }}
                data-testid="gb-clan-tank-color">
                <span className="h-6 w-6 flex-none rounded-lg border border-black/50"
                  style={{ background: escolhida?.hex || 'transparent',
                    boxShadow: escolhida ? `0 0 14px ${escolhida.hex}` : undefined }} />
                <p className="min-w-0 text-[11px] font-bold text-purple-100">
                  {escolhida
                    ? <>cor dos tanques: <b style={{ color: escolhida.hex }}>{escolhida.cor}</b> — vem da medalha {escolhida.nome} e não pode ser trocada</>
                    : 'escolha a medalha para ver a cor dos tanques do seu clã'}
                </p>
              </div>
            </Passo>
          </div>

          <button
            disabled={busy || !podeCriar || !form.name || !form.medal}
            onClick={() => acao(async () => {
              await gbApi.post('/clans', form);
              gbAviso.ok('Clã fundado! Medalha e cor já são suas na batalha.');
              setForm({ name: '', medal: '' });
            })}
            data-testid="gb-clan-submit"
            className="mt-4 flex w-full items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-orange-600 via-orange-500 to-amber-400 px-5 py-3.5 text-sm font-black uppercase tracking-wider text-[#170a01] shadow-[0_0_25px_rgba(249,115,22,.45)] transition-all hover:scale-[1.01] hover:shadow-[0_0_35px_rgba(249,115,22,.65)] disabled:opacity-40 disabled:shadow-none disabled:hover:scale-100">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Gem className="h-4 w-4" />}
            fundar por {gbFmt(regras.cost)} diamantes
          </button>
          {!podeCriar && (
            <p className="mt-2 text-center text-[11px] font-bold text-rose-300/80" data-testid="gb-clan-blocked">
              {regras.slots_left === 0 ? `Os ${regras.max_clans} clãs já existem — espere algum ser dissolvido.`
                : me.level < regras.min_level ? `Você é nível ${me.level}. Precisa de nível ${regras.min_level} (o XP vem principalmente do PvP).`
                  : `Faltam ${gbFmt(regras.cost - me.diamonds)} diamantes para fundar.`}
            </p>
          )}
        </div>
      )}

      {/* ── clãs do mundo ── */}
      <h3 className="mb-2.5 mt-5 flex items-center gap-2 text-sm font-black uppercase tracking-wider text-orange-300">
        <Shield className="h-4 w-4" />clãs do mundo
        <span className="rounded-full bg-orange-600/20 px-2 py-[1px] text-[10px] font-black text-orange-200">
          {lista.length}
        </span>
      </h3>
      {lista.length === 0 && (
        <p className="rounded-2xl border border-dashed border-purple-800/60 bg-black/25 px-4 py-6 text-center text-xs text-purple-300/60">
          Nenhum clã fundado ainda — seja o primeiro a erguer uma bandeira.
        </p>
      )}
      <div className="custom-scrollbar max-h-[58vh] overflow-y-auto overscroll-contain pr-1 xl:max-h-[520px]"
        data-testid="gb-clans-scroll">
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          {lista.map((c) => {
          const lider = (c.members || []).find((m) => m.leader);
          const cheio = c.members_count >= regras.max_members;
          return (
            <div key={c.clan_id} data-testid={`gb-clan-${c.clan_id}`}
              className="relative overflow-hidden rounded-2xl border p-3.5 transition-transform hover:-translate-y-0.5"
              style={{ borderColor: `${c.hex}55`, background: `linear-gradient(160deg, ${c.hex}1c, rgba(13,5,23,.95) 60%)` }}>
              <div className="flex items-start gap-3">
                <ClanMedal clan={c} className="h-14 w-14" testid={`gb-clan-medal-img-${c.clan_id}`} />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-black text-purple-50">{c.name}</p>
                  <p className="mt-1 flex items-center gap-1.5 text-[10px] font-bold text-purple-300/60">
                    <Avatar src={c.leader_avatar} nome={lider?.nickname} className="h-5 w-5" cor="#fbbf24" />
                    <Crown className="h-3 w-3 text-amber-300" />
                    <span className="truncate">{lider?.nickname || 'líder'}</span>
                  </p>
                </div>
              </div>
              <div className="mt-3 flex flex-wrap items-center gap-1.5">
                <Chip Icon={Users}>{c.members_count}/{regras.max_members}</Chip>
                <Chip Icon={Trophy} tone="emerald">{gbFmt(c.pvp_wins)} vit.</Chip>
                <Chip Icon={Swords} tone="amber">{gbFmt(c.points)} pts</Chip>
              </div>
              <span className="mt-2.5 block h-1.5 w-full overflow-hidden rounded-full bg-black/50">
                <span className="block h-full rounded-full"
                  style={{ width: `${Math.round((c.members_count / regras.max_members) * 100)}%`, background: c.hex }} />
              </span>
              {!meu && (
                c.pedi
                  ? <button onClick={() => acao(() => gbApi.delete(`/clans/${c.clan_id}/request`))}
                    data-testid={`gb-clan-cancel-${c.clan_id}`}
                    className="mt-3 w-full rounded-xl border border-purple-700/60 bg-black/40 py-2 text-[11px] font-black text-purple-200 transition-colors hover:border-rose-600 hover:text-rose-200">
                    cancelar pedido
                  </button>
                  : <button onClick={() => acao(async () => {
                    await gbApi.post(`/clans/${c.clan_id}/request`);
                    gbAviso.ok('Pedido enviado ao líder.');
                  })} disabled={busy || cheio}
                    data-testid={`gb-clan-join-${c.clan_id}`}
                    className="mt-3 flex w-full items-center justify-center gap-1.5 rounded-xl bg-gradient-to-r from-purple-700 to-purple-600 py-2 text-[11px] font-black uppercase tracking-wider text-purple-50 transition-transform hover:scale-[1.01] disabled:opacity-40 disabled:hover:scale-100">
                    <UserPlus className="h-3.5 w-3.5" />{cheio ? 'clã cheio' : 'pedir para entrar'}
                  </button>
              )}
            </div>
          );
          })}
        </div>
      </div>
    </div>
  );
};
